import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Search,
  Download,
  Star,
  Shield,
  Zap,
  Globe,
  Code2,
  Image,
  Bot,
  MessageSquare,
  Database,
  FileText,
  Palette,
  Box,
  Terminal,
  Workflow,
  Lock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ExternalLink,
  Tag,
} from 'lucide-react';
import Navbar from '../src/components/landing/Navbar';
import Footer from '../src/components/landing/Footer';

import '../pages/landing.module.css';

/* ── Types ────────────────────────────────────────────────────────── */

interface SkillEntry {
  name: string;
  slug: string;
  description: string;
  category: string;
  author: string;
  tags: string[];
  source: string;
  githubUrl: string;
}

interface Pagination {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

/* ── Category → Icon map ──────────────────────────────────────────── */

const categoryIcons: Record<string, React.ReactNode> = {
  automation: <Globe size={18} />,
  browser: <Globe size={18} />,
  media: <Image size={18} />,
  image: <Palette size={18} />,
  '3d': <Box size={18} />,
  code: <Code2 size={18} />,
  messaging: <MessageSquare size={18} />,
  integration: <Zap size={18} />,
  planning: <Workflow size={18} />,
  document: <FileText size={18} />,
  security: <Lock size={18} />,
  research: <Database size={18} />,
  ai: <Bot size={18} />,
  terminal: <Terminal size={18} />,
  general: <Star size={18} />,
};

function getIconForSkill(skill: SkillEntry): React.ReactNode {
  const cat = skill.category.toLowerCase();
  if (categoryIcons[cat]) return categoryIcons[cat];
  for (const tag of skill.tags) {
    const t = tag.toLowerCase();
    if (categoryIcons[t]) return categoryIcons[t];
  }
  return <Zap size={18} />;
}

/* ── Skill Sources (static) ───────────────────────────────────────── */

const skillSources = [
  { name: 'Built-in Skills', count: '20+', description: 'Core skills shipped with Tasking.tech' },
  { name: 'tippyentertainment/skills', count: '50+', description: 'Curated community skills from TippyEntertainment' },
  { name: 'tasking-tech/skills', count: '30+', description: 'Official Tasking.tech skill extensions' },
  { name: 'OpenClaw Skills', count: '100+', description: 'Compatible skills from the OpenClaw ecosystem' },
  { name: 'ClawdHub', count: '200+', description: 'Third-party skill marketplace integration' },
  { name: 'Custom Skills', count: 'Unlimited', description: 'Build and install your own skills' },
];

/* ── Page ──────────────────────────────────────────────────────────── */

const SkillsPage = () => {
  const [skills, setSkills] = useState<SkillEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, perPage: 30, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const skillsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      document.body.style.overflow = 'auto';
    }
    return () => {
      if (root) {
        root.style.overflow = '';
        root.style.height = '';
        document.body.style.overflow = '';
      }
    };
  }, []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  // Fetch skills
  const fetchSkills = useCallback(async (page: number, query: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: '30' });
      if (query) params.set('q', query);
      const res = await fetch(`/_api/public-skills?${params}`);
      if (res.ok) {
        const data = await res.json();
        setSkills(data.skills || []);
        setPagination(data.pagination || { page: 1, perPage: 30, total: 0, totalPages: 0 });
      }
    } catch (err) {
      console.error('[Skills] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset to page 1 on search change
  useEffect(() => {
    fetchSkills(1, debouncedQuery);
  }, [debouncedQuery, fetchSkills]);

  const goToPage = (page: number) => {
    fetchSkills(page, debouncedQuery);
    skillsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-background text-foreground overflow-auto">
      <>
        <title>Skills Marketplace | Tasking.tech</title>
        <meta
          name="description"
          content="Browse and install skills for Tasking.tech. Browser automation, image generation, code execution, messaging integrations, and hundreds more from our community marketplace."
        />
        <meta property="og:title" content="Skills Marketplace | Tasking.tech" />
        <meta property="og:description" content="Extend your AI agents with powerful skills from our marketplace." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://tasking.tech/skills" />
        <link rel="canonical" href="https://tasking.tech/skills" />
      </>

      <Navbar />

      {/* Hero */}
      <section className="section-padding pt-32">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft size={14} /> Back to home
            </Link>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Skills <span className="text-gradient">Marketplace</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Extend your AI agents with powerful skills. Browse built-in capabilities,
              community contributions, and install custom skills from multiple sources.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Skill Sources */}
      <section className="pb-12">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-2xl font-bold text-foreground mb-6">Skill Sources</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {skillSources.map((source, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="glass-card-hover rounded-xl p-5"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">{source.name}</h3>
                  <span className="rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2.5 py-0.5 text-xs font-semibold">
                    {source.count}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{source.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Browser */}
      <section className="pb-16" ref={skillsRef}>
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h2 className="text-2xl font-bold text-foreground">Browse Skills</h2>
            <div className="relative w-full sm:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-border/50 bg-secondary/50 pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-colors"
              />
            </div>
          </div>

          {/* Results count */}
          {!loading && (
            <p className="text-xs text-muted-foreground mb-4">
              {pagination.total} skill{pagination.total !== 1 ? 's' : ''} found
              {debouncedQuery ? ` for "${debouncedQuery}"` : ''}
              {pagination.totalPages > 1 ? ` — page ${pagination.page} of ${pagination.totalPages}` : ''}
            </p>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-3 text-sm text-muted-foreground">Loading skills from GitHub...</span>
            </div>
          )}

          {/* Skills Grid */}
          {!loading && skills.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {skills.map((skill, i) => (
                <motion.div
                  key={skill.slug}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="glass-card-hover rounded-xl p-5"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-500/15 text-orange-400">
                      {getIconForSkill(skill)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground truncate">{skill.name}</h3>
                        {skill.githubUrl && (
                          <a
                            href={skill.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-muted-foreground/50 hover:text-foreground transition-colors"
                            title="View on GitHub"
                          >
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed line-clamp-2">{skill.description}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="rounded-full bg-green-500/15 text-green-400 border border-green-500/20 px-2 py-0.5 text-[10px] font-medium">
                          {skill.category}
                        </span>
                        {skill.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full bg-white/5 text-muted-foreground border border-border/30 px-2 py-0.5 text-[10px] font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <p className="text-[10px] text-muted-foreground/60">{skill.source}</p>
                        {skill.author !== 'community' && (
                          <p className="text-[10px] text-muted-foreground/40">by {skill.author}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && skills.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <Search size={32} className="text-muted-foreground/30 mb-4" />
              <p className="text-sm text-muted-foreground">
                {debouncedQuery ? `No skills found for "${debouncedQuery}"` : 'No skills available'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {!loading && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="flex items-center gap-1 rounded-lg border border-border/50 bg-secondary/50 px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} /> Prev
              </button>
              {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (pagination.totalPages <= 7) {
                  pageNum = i + 1;
                } else if (pagination.page <= 4) {
                  pageNum = i + 1;
                } else if (pagination.page >= pagination.totalPages - 3) {
                  pageNum = pagination.totalPages - 6 + i;
                } else {
                  pageNum = pagination.page - 3 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`rounded-lg px-3 py-2 text-xs font-medium transition-colors ${
                      pageNum === pagination.page
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border/50 bg-secondary/50 text-foreground hover:border-primary/30'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="flex items-center gap-1 rounded-lg border border-border/50 bg-secondary/50 px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/30 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-10 text-center"
          >
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Build your own skills
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Create custom skills for your team or publish them to the community.
              Skills are simple definitions — a name, description, and execution logic.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/login"
                className="rounded-lg bg-primary px-8 py-3 text-lg font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                Get Started
              </Link>
              <a
                href="https://github.com/tippyentertainment/skills"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-lg border border-border bg-secondary px-8 py-3 text-lg font-semibold text-foreground transition-colors hover:border-primary/30"
              >
                Browse on GitHub
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SkillsPage;
